import {
  BaseModalConfig,
  ModalComponentInputs
} from "src/app/modals/base-modal/base-modal.component";
import { AppModalOptions } from "./modal.service";
import { EmployeePINPage } from "src/app/modals/inner-components/employee-pin/employee-pin.page";
import { AdditionalSubjModalPage } from "src/app/modals/inner-components/additional-subj-modal/additional-subj-modal.page";
import { CallModalComponent } from "src/app/modals/inner-components/call-modal/call-modal.component";
import { ChatModalComponent } from "src/app/modals/inner-components/chat-modal/chat-modal.component";
import { LeaveMessageModalComponent } from "src/app/modals/inner-components/leave-message-modal/leave-message-modal.component";
import { PrivacyPolicyModalPage } from "src/app/modals/inner-components/privacy-policy-modal/privacy-policy-modal.page";
import {
  DoorBellData,
  ChannelData,
  CallModalButton
} from "src/app/interfaces/interfaces";
import { StickyErrorModalComponent } from "src/app/modals/inner-components/sticky-error-modal/sticky-error-modal.component";
import {
  ConditionalModalOptions,
  ConditionComponent
} from "src/app/modals/inner-components/condition/condition.component";
import {
  InformationComponent,
  InformationModalOptions
} from "src/app/modals/inner-components/information/information.component";
import { InformationPage } from "src/app/pages/information/information.page";
import {
  ConnectorErrorModalOptions
} from "src/app/modals/inner-components/connector-error-modal/connector-error-modal.component";

const DEFAULT_BASE_CONFIG: BaseModalConfig = {
  showHeader: true,
  showCloseButton: true,
  headerWithShadow: false,
  centerHeaderTitle: false,
  centerInnerContent: true,
  showFooter: false,
  showFooterButton: false,
  showFooterShadow: false,
  footerButtonText: "BTN.OK",
  footerButtonColor: "dark",
  footerButtonDisabled: false,
  footerColumeSize: "auto",
  footerButtonExpandMode: null,
  autoCloseEnabled: true,
  autoCloseTimeout: 120
};

export const ModalTypes = {
  employeePin: (
    opts: { showNfcOption?: boolean; errorMessage?: string } = {}
  ): AppModalOptions => ({
    componentProps: {
      config: {
        innerComponent: EmployeePINPage,
        header: "",
        message: "",
        showHeader: false,
        showCloseButton: false,
        accessPinCloseButton: true,
        showFooter: false,
        centerInnerContent: false,
        autoCloseEnabled: true,
        autoCloseTimeout: 120,
        innerComponentInputs: {
          showNfcOption: opts.showNfcOption ?? false,
          errorMessage: opts.errorMessage ?? ""
        } satisfies Partial<ModalComponentInputs<EmployeePINPage>>
      }
    },
    backdropDismiss: false,
    keyboardClose: false,
    cssClass: "transparent-modal access-pin-modal-class"
  }),

  additionalConcern: (opts: { sub: DoorBellData[]; isEmpPIN: boolean; EmpPIN: string }): AppModalOptions => ({
    componentProps: {
      config: {
        innerComponent: AdditionalSubjModalPage,
        header: "",
        message: "",
        showHeader: false,
        showCloseButton: false,
        accessPinCloseButton: true,
        showFooter: false,
        centerInnerContent: false,
        autoCloseEnabled: true,
        autoCloseTimeout: 120,
        innerComponentInputs: {
          sub: opts.sub,
          isEmpPIN: opts.isEmpPIN,
          EmpPIN: opts.EmpPIN
        } satisfies Partial<ModalComponentInputs<AdditionalSubjModalPage>>
      }
    },
    backdropDismiss: false,
    keyboardClose: false,
    cssClass: "transparent-modal concern-modal-class"
  }),

  callModal: (
    opts: {
      button: CallModalButton
    },
    configOverride: Partial<BaseModalConfig> = {}
  ): AppModalOptions => ({
    componentProps: {
      config: {
        ...DEFAULT_BASE_CONFIG,
        innerComponent: CallModalComponent,
        header: opts.button.buttonName,
        message: "",
        showHeader: false,
        showCloseButton: false,
        centerInnerContent: false,
        showFooterShadow: false,
        headerWithShadow: true,
        autoCloseEnabled: true,
        autoCloseTimeout: 120,
        innerComponentInputs: {
          button: opts.button,
        } satisfies Partial<ModalComponentInputs<CallModalComponent>>,
        ...configOverride
      }
    },
    backdropDismiss: false,
    keyboardClose: false,
    cssClass: "transparent-modal call-modal-class"
  }),

  leaveMessage: (
    opts: {
      buttonName: string;
      resourceId: string;
      channel: ChannelData;
      team: string;
      messageId: string;
      connectorId: string;
      connectionId?: string;
      infoMessage?: string;
      isPortrait?: boolean;
      sendAsChannelMessage?: boolean;
    },
    configOverride: Partial<BaseModalConfig> = {}
  ): AppModalOptions => ({
    componentProps: {
      config: {
        ...DEFAULT_BASE_CONFIG,
        innerComponent: LeaveMessageModalComponent,
        showHeader: false,
        showCloseButton: false,
        showFooter: false,
        autoCloseEnabled: true,
        autoCloseTimeout: 120,
        centerInnerContent: false,
        innerComponentInputs: {
          config: {
            buttonName: opts.buttonName,
            resourceId: opts.resourceId,
            channel: opts.channel,
            team: opts.team,
            messageId: opts.messageId,
            connectorId: opts.connectorId,
            connectionId: opts.connectionId,
            infoMessage: opts.infoMessage,
            isPortrait: opts.isPortrait,
            sendAsChannelMessage: opts.sendAsChannelMessage
          }
        } satisfies Partial<ModalComponentInputs<LeaveMessageModalComponent>>,
        ...configOverride
      }
    },
    backdropDismiss: false,
    keyboardClose: false,
    cssClass: "transparent-modal leave-message-modal-class"
  }),

  chatModal: (type: string, message: string, data: any = {}): AppModalOptions => ({
    componentProps: {
      config: {
        innerComponent: ChatModalComponent,
        showHeader: false,
        showCloseButton: false,
        showFooter: false,
        centerInnerContent: false,
        autoCloseEnabled: type !== "waiting" && type !== "leave-message",
        autoCloseTimeout: data.autoCloseTimeout ?? 120,
        innerComponentInputs: {
          config: {
            type: type,
            message: message,
            waitingTime: data.waitingTime,
            checkResponse: data.checkResponse,
            onWaitingSuccess: data.onWaitingSuccess,
            onWaitingTimeout: data.onWaitingTimeout,
            onWaitingClose: data.onWaitingClose,
            onLeaveMessage: data.onLeaveMessage
          }
        } satisfies Partial<ModalComponentInputs<ChatModalComponent>>
      }
    },
    backdropDismiss: false,
    keyboardClose: false,
    cssClass: "transparent-modal chat-modal-class"
  }),

  conditional: (
    opts: ConditionalModalOptions = {},
    configOverride: Partial<BaseModalConfig> = {}
  ): AppModalOptions => ({
    componentProps: {
      config: {
        ...DEFAULT_BASE_CONFIG,
        message: "MODAL.DEFAULTMESSAGE.CONDITIONAL",
        innerComponent: ConditionComponent,
        showHeader: false,
        showCloseButton: false,
        showFooter: false,
        autoCloseEnabled: true,
        autoCloseTimeout: 120,
        ...configOverride,
        innerComponentInputs: {
          config: {
            ...opts,
            ...(configOverride.innerComponentInputs || {})
          }
        } satisfies Partial<ModalComponentInputs<ConditionComponent>>
      }
    },
    backdropDismiss: false,
    keyboardClose: false
  }),

  privacyConditional: (
    opts: { url: string },
    configOverride: Partial<BaseModalConfig> = {}
  ): AppModalOptions => ({
    componentProps: {
      config: {
        ...DEFAULT_BASE_CONFIG,
        innerComponent: PrivacyPolicyModalPage,
        header: "MODAL.HEADER.PRIVACYPOLICYHEADER",
        message: "MODAL.DEFAULTMESSAGE.PRIVACYCONDITIONAL",
        showHeader: true,
        showCloseButton: false,
        showFooter: false,
        headerWithShadow: true,
        centerHeaderTitle: false,
        autoCloseEnabled: true,
        autoCloseTimeout: 120,
        innerComponentInputs: {
          url: opts.url
        } satisfies Partial<ModalComponentInputs<PrivacyPolicyModalPage>>,
        ...configOverride
      }
    },
    backdropDismiss: false,
    keyboardClose: false
  }),

  stickyErrorModal: (
    messageKey: string,
    configOverride: Partial<BaseModalConfig> = {}
  ): AppModalOptions => ({
    componentProps: {
      config: {
        ...DEFAULT_BASE_CONFIG,
        message: messageKey,
        showHeader: false,
        showFooter: false,
        autoCloseEnabled: true,
        autoCloseTimeout: 120,
        innerComponent: StickyErrorModalComponent,
        ...configOverride
      }
    },
    backdropDismiss: false,
    keyboardClose: false
  }),

  error: (
    opts: InformationModalOptions = {},
    configOverride: Partial<BaseModalConfig> = {}
  ): AppModalOptions => ({
    componentProps: {
      config: {
        ...DEFAULT_BASE_CONFIG,
        innerComponent: InformationComponent,
        showHeader: false,
        showCloseButton: false,
        showFooter: false,
        autoCloseEnabled: true,
        autoCloseTimeout: 120,
        ...configOverride,
        innerComponentInputs: {
          config: {
            message: "MODAL.DEFAULTMESSAGE.ERROR",
            iconName: "alert-circle-outline",
            iconColor: "danger",
            buttonText: "BTN.OK",
            buttonColor: "dark",

            ...opts,
            ...(configOverride.innerComponentInputs || {})
          }
        } satisfies Partial<ModalComponentInputs<InformationComponent>>
      }
    },
    backdropDismiss: true
  }),

  success: (
    opts: InformationModalOptions = {},
    configOverride: Partial<BaseModalConfig> = {}
  ): AppModalOptions => ({
    componentProps: {
      config: {
        ...DEFAULT_BASE_CONFIG,
        innerComponent: InformationComponent,
        showHeader: false,
        showCloseButton: false,
        showFooter: false,
        autoCloseEnabled: true,
        autoCloseTimeout: 120,
        ...configOverride,
        innerComponentInputs: {
          config: {
            message: "MODAL.DEFAULTMESSAGE.SUCCESS",
            iconName: "checkmark-circle-outline",
            iconColor: "success",
            buttonText: "BTN.OK",
            buttonColor: "dark",

            ...opts,
            ...(configOverride.innerComponentInputs || {})
          }
        } satisfies Partial<ModalComponentInputs<InformationComponent>>
      }
    },
    backdropDismiss: true
  }),

  navigate: (
    opts: InformationModalOptions = {},
    configOverride: Partial<BaseModalConfig> = {}
  ): AppModalOptions => ({
    componentProps: {
      config: {
        ...DEFAULT_BASE_CONFIG,
        innerComponent: InformationComponent,
        showHeader: false,
        showCloseButton: false,
        showFooter: false,
        autoCloseEnabled: true,
        autoCloseTimeout: 120,

        ...configOverride,
        innerComponentInputs: {
          config: {
            message: "MODAL.DEFAULTMESSAGE.CONDITIONAL",
            iconName: "checkmark-circle-outline",
            iconColor: "yellow",
            buttonText: "BTN.OK",
            buttonColor: "green",

            ...opts,
            ...(configOverride.innerComponentInputs || {})
          }
        } satisfies Partial<ModalComponentInputs<InformationComponent>>
      }
    },

    backdropDismiss: true
  }),

  warning: (
    opts: InformationModalOptions = {},
    configOverride: Partial<BaseModalConfig> = {}
  ): AppModalOptions => ({
    componentProps: {
      config: {
        ...DEFAULT_BASE_CONFIG,
        innerComponent: InformationComponent,
        showHeader: false,
        showCloseButton: false,
        showFooter: false,
        autoCloseEnabled: true,
        autoCloseTimeout: 120,
        ...configOverride,
        innerComponentInputs: {
          config: {
            message: "MODAL.DEFAULTMESSAGE.WARNING",
            iconName: "warning-outline",
            iconColor: "warning",
            buttonText: "BTN.OK",
            buttonColor: "dark",

            ...opts,
            ...(configOverride.innerComponentInputs || {})
          }
        } satisfies Partial<ModalComponentInputs<InformationComponent>>
      }
    },
    backdropDismiss: true
  }),

  informationPage: (
    opts: ConditionalModalOptions = {},
    configOverride: Partial<BaseModalConfig> = {}
  ): AppModalOptions => ({
    componentProps: {
      config: {
        ...DEFAULT_BASE_CONFIG,
        innerComponent: InformationPage,
        showHeader: false,
        showCloseButton: false,
        showFooter: false,
        autoCloseEnabled: true,
        autoCloseTimeout: 120,
        centerInnerContent: false,
        ...configOverride,
        innerComponentInputs: {
          ...opts,
          ...(configOverride.innerComponentInputs || {})
        }
      }
    },
    cssClass: "informationModal"
  }),

  connectorError: (
    opts: ConnectorErrorModalOptions = {},
    configOverride: Partial<BaseModalConfig> = {}
  ): AppModalOptions => ({
    componentProps: {
      config: {
        ...DEFAULT_BASE_CONFIG,
        innerComponent: ChatModalComponent,
        showHeader: false,
        showCloseButton: false,
        showFooter: false,
        autoCloseEnabled: true,
        autoCloseTimeout: 120,
        centerInnerContent: false,
        ...configOverride,
        innerComponentInputs: {
          config: {
            type: "failed",
            message: opts.message ?? "CONNECTOR_ERROR.MESSAGE",
            ...(configOverride.innerComponentInputs || {})
          }
        } satisfies Partial<ModalComponentInputs<ChatModalComponent>>
      }
    },
    backdropDismiss: false,
    keyboardClose: false,
    cssClass: "transparent-modal chat-modal-class"
  }),
};